import React, { createContext, useContext, useState, useRef, useMemo, ReactNode } from 'react';
import { IDemandsData } from '@/types';
import { useStableCallback } from '@hooks/useStableCallback';

export interface DateRangeValue {
  type: 'today' | 'week' | 'month' | 'year' | 'custom' | 'all';
  startDate: Date | null;
  endDate: Date | null;
  label: string;
}

interface DemandsContextType {
  demandsData: IDemandsData[];
  setDemandsData: (data: IDemandsData[]) => void;
  selectedClients: string[];
  setSelectedClients: (clients: string[]) => void;
  ensureSingleClientSelected: (availableClientIds: string[]) => void;
  dateRange: DateRangeValue;
  setDateRange: (range: DateRangeValue, page?: 'demands' | 'reports') => void;
  reportsDateType: 'createdAt' | 'completedAt';
  setReportsDateType: (type: 'createdAt' | 'completedAt') => void;
  demandsDateType: 'createdAt' | 'estimatedCompletion';
  setDemandsDateType: (type: 'createdAt' | 'estimatedCompletion') => void;
  selectedCategories: string[];
  setSelectedCategories: (categories: string[]) => void;
  selectedStatuses: string[];
  setSelectedStatuses: (statuses: string[]) => void;
  selectedQrCodeIdentifiers: string[];
  setSelectedQrCodeIdentifiers: (identifiers: string[]) => void;
  selectedPriorities: string[];
  setSelectedPriorities: (priorities: string[]) => void;
  /** Filtro por responsável (assigneeId) — mesmo padrão de categoria/prioridade. */
  selectedAssigneeIds: string[];
  setSelectedAssigneeIds: (assigneeIds: string[]) => void;
  /** Filtro por prestador (providerId) — mesmo padrão acima. */
  selectedProviderIds: string[];
  setSelectedProviderIds: (providerIds: string[]) => void;
  showSimilarOnly: boolean;
  setShowSimilarOnly: (value: boolean) => void;
  /** Busca por texto no board (número/descrição) — mesmo campo do mobile. Não persiste. */
  searchText: string;
  setSearchText: (value: string) => void;
  /** Atalho "Demandas atrasadas" do sino — restringe o board às demandas em aberto/andamento com
   * prazo vencido (ver classifyDemandDeadlineStatus). Não persiste em localStorage, filtro pontual. */
  overdueOnly: boolean;
  setOverdueOnly: (value: boolean) => void;
  /** Atalho "Checklists pendentes" do sino — `null` = sem restrição; array (mesmo vazio) restringe
   * o board a esses IDs (GET /admin/notifications/pending-checklist-demand-ids). Não persiste. */
  checklistPendingDemandIds: string[] | null;
  setChecklistPendingDemandIds: (ids: string[] | null) => void;
  /** Atalho "Prestadores aguardando WhatsApp" do sino (Fase 5) — mesma não-persistência acima. */
  whatsappPendingOnly: boolean; setWhatsappPendingOnly: (value: boolean) => void;
  refreshDemands: () => void | Promise<void>;
  setRefreshCallback: (callback: (() => void | Promise<void>) | null) => void;
  getStoredDateRange: (page: 'demands' | 'reports') => DateRangeValue | null;
}

const DemandsContext = createContext<DemandsContextType | undefined>(undefined);

export const useDemands = () => {
  const context = useContext(DemandsContext);
  if (!context) {
    throw new Error('useDemands must be used within a DemandsProvider');
  }
  return context;
};

interface DemandsProviderProps {
  children: ReactNode;
}

// Funções auxiliares para localStorage
const STORAGE_KEYS = {
  DEMANDS_DATE_RANGE: 'vover_demands_date_range',
  REPORTS_DATE_RANGE: 'vover_reports_date_range',
  DEMANDS_DATE_TYPE: 'vover_demands_date_type',
  DEMANDS_CATEGORY: 'vover_demands_category',
  DEMANDS_STATUS: 'vover_demands_status',
  DEMANDS_QRCODE_IDENTIFIER: 'vover_demands_qrcode_identifier',
  DEMANDS_PRIORITY: 'vover_demands_priority',
  DEMANDS_ASSIGNEE: 'vover_demands_assignee',
  DEMANDS_PROVIDER: 'vover_demands_provider',
  SELECTED_CLIENTS: 'vover_selected_clients'
};

const saveDateRangeToStorage = (page: 'demands' | 'reports', range: DateRangeValue) => {
  try {
    const key = page === 'demands' ? STORAGE_KEYS.DEMANDS_DATE_RANGE : STORAGE_KEYS.REPORTS_DATE_RANGE;
    // Salvar apenas o tipo e, se custom, as datas no formato yyyy-MM-dd (sem timezone)
    const toStore: { type: string; label: string; startDate?: string; endDate?: string } = {
      type: range.type,
      label: range.label,
    };

    if (range.type === 'custom' && range.startDate && range.endDate) {
      const formatYmd = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      toStore.startDate = formatYmd(range.startDate);
      toStore.endDate = formatYmd(range.endDate);
    }

    localStorage.setItem(key, JSON.stringify(toStore));
  } catch (error) {
    // Silently fail - localStorage errors are not critical
  }
};

const getDateRangeFromStorage = (page: 'demands' | 'reports'): DateRangeValue | null => {
  try {
    const key = page === 'demands' ? STORAGE_KEYS.DEMANDS_DATE_RANGE : STORAGE_KEYS.REPORTS_DATE_RANGE;
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored);
    // Se for custom, reconstruir as datas
    if (parsed.type === 'custom' && parsed.startDate && parsed.endDate) {
      const parseYmd = (value: string): Date => {
        let dateString = value;
        // Compatibilidade com formato ISO antigo: pegar apenas a parte yyyy-MM-dd
        if (dateString.length > 10) {
          dateString = dateString.slice(0, 10);
        }
        const [yearStr, monthStr, dayStr] = dateString.split('-');
        const year = Number(yearStr);
        const month = Number(monthStr);
        const day = Number(dayStr);
        return new Date(year, month - 1, day);
      };

      return {
        type: parsed.type,
        label: parsed.label,
        startDate: parseYmd(parsed.startDate),
        endDate: parseYmd(parsed.endDate)
      };
    }
    
    // Para outros tipos, retornar apenas o tipo (as datas serão calculadas)
    return {
      type: parsed.type,
      label: parsed.label,
      startDate: null,
      endDate: null
    };
  } catch (error) {
    // Silently fail - localStorage errors are not critical
    return null;
  }
};

const parseStoredArray = (stored: string | null): string[] => {
  try {
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed)) return parsed.filter((x): x is string => typeof x === 'string');
    if (typeof parsed === 'string' && parsed) return [parsed]; // migrar valor antigo
    return [];
  } catch {
    return [];
  }
};

export const DemandsProvider: React.FC<DemandsProviderProps> = ({ children }) => {
  const [demandsData, setDemandsData] = useState<IDemandsData[]>([]);
  // Condomínio selecionado sobrevive a navegação entre páginas E a logout/login (mesma lógica de
  // persistência das outras chaves DEMANDS_* abaixo) — ver `ensureSingleClientSelected`, que decide
  // qual condomínio fica selecionado quando a página carrega sem que o usuário tenha escolhido nada.
  const [selectedClients, setSelectedClientsState] = useState<string[]>(() => {
    try {
      return parseStoredArray(localStorage.getItem(STORAGE_KEYS.SELECTED_CLIENTS));
    } catch {
      return [];
    }
  });
  const [dateRange, setDateRangeState] = useState<DateRangeValue>({
    type: 'all',
    startDate: null,
    endDate: null,
    label: 'Todos os períodos'
  });
  const [reportsDateType, setReportsDateTypeState] = useState<'createdAt' | 'completedAt'>('createdAt');
  
  const setReportsDateType = (type: 'createdAt' | 'completedAt') => {
    setReportsDateTypeState(type);
    // Não precisa salvar no localStorage para reports, pois é específico da página
  };
  
  // Carregar demandsDateType do localStorage
  const getStoredDemandsDateType = (): 'createdAt' | 'estimatedCompletion' => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.DEMANDS_DATE_TYPE);
      if (stored && (stored === 'createdAt' || stored === 'estimatedCompletion')) {
        return stored as 'createdAt' | 'estimatedCompletion';
      }
    } catch (error) {
      // Silently fail
    }
    return 'createdAt'; // Default
  };
  
  const [demandsDateType, setDemandsDateTypeState] = useState<'createdAt' | 'estimatedCompletion'>(getStoredDemandsDateType());
  const [selectedCategories, setSelectedCategoriesState] = useState<string[]>(() => {
    try {
      return parseStoredArray(localStorage.getItem(STORAGE_KEYS.DEMANDS_CATEGORY));
    } catch {
      return [];
    }
  });
  const [selectedStatuses, setSelectedStatusesState] = useState<string[]>(() => {
    try {
      return parseStoredArray(localStorage.getItem(STORAGE_KEYS.DEMANDS_STATUS));
    } catch {
      return [];
    }
  });
  const [selectedQrCodeIdentifiers, setSelectedQrCodeIdentifiersState] = useState<string[]>(() => {
    try {
      return parseStoredArray(localStorage.getItem(STORAGE_KEYS.DEMANDS_QRCODE_IDENTIFIER));
    } catch {
      return [];
    }
  });
  const [selectedPriorities, setSelectedPrioritiesState] = useState<string[]>(() => {
    try {
      return parseStoredArray(localStorage.getItem(STORAGE_KEYS.DEMANDS_PRIORITY));
    } catch {
      return [];
    }
  });
  const [selectedAssigneeIds, setSelectedAssigneeIdsState] = useState<string[]>(() => {
    try {
      return parseStoredArray(localStorage.getItem(STORAGE_KEYS.DEMANDS_ASSIGNEE));
    } catch {
      return [];
    }
  });
  const [selectedProviderIds, setSelectedProviderIdsState] = useState<string[]>(() => {
    try {
      return parseStoredArray(localStorage.getItem(STORAGE_KEYS.DEMANDS_PROVIDER));
    } catch {
      return [];
    }
  });
  const [showSimilarOnly, setShowSimilarOnly] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [overdueOnly, setOverdueOnly] = useState<boolean>(false);
  const [checklistPendingDemandIds, setChecklistPendingDemandIds] = useState<string[] | null>(null);
  const [whatsappPendingOnly, setWhatsappPendingOnly] = useState<boolean>(false);
  const refreshCallbackRef = useRef<(() => void | Promise<void>) | null>(null);
  
  const setSelectedClients = (clients: string[]) => {
    setSelectedClientsState(clients);
    try {
      if (clients.length > 0) {
        localStorage.setItem(STORAGE_KEYS.SELECTED_CLIENTS, JSON.stringify(clients));
      } else {
        localStorage.removeItem(STORAGE_KEYS.SELECTED_CLIENTS);
      }
    } catch (error) {
      // Silently fail
    }
  };

  // Dono único da regra "qual condomínio fica selecionado ao carregar uma página" — chamado pelos
  // loaders de Demandas/Relatórios (Sugestões herda o estado do contexto, sem loader próprio) em vez
  // de cada página decidir por conta própria (a causa raiz da seleção "quebrando" entre páginas:
  // cada uma tinha sua própria lógica ad-hoc e nenhuma persistia em localStorage). Mantém a seleção
  // atual se ainda for válida na página que está carregando; senão usa o primeiro da lista recebida
  // (já vem ordenada alfabeticamente por quem chama). Não faz nada se a lista vier vazia.
  const ensureSingleClientSelected = (availableClientIds: string[]) => {
    const firstAvailable = availableClientIds[0];
    if (!firstAvailable) return;
    const current = selectedClients[0];
    if (current && availableClientIds.includes(current)) return;
    setSelectedClients([firstAvailable]);
  };

  const setDemandsDateType = (type: 'createdAt' | 'estimatedCompletion') => {
    setDemandsDateTypeState(type);
    try {
      localStorage.setItem(STORAGE_KEYS.DEMANDS_DATE_TYPE, type);
    } catch (error) {
      // Silently fail
    }
  };
  
  const setSelectedCategories = (categories: string[]) => {
    setSelectedCategoriesState(categories);
    try {
      if (categories.length > 0) {
        localStorage.setItem(STORAGE_KEYS.DEMANDS_CATEGORY, JSON.stringify(categories));
      } else {
        localStorage.removeItem(STORAGE_KEYS.DEMANDS_CATEGORY);
      }
    } catch (error) {
      // Silently fail
    }
  };

  const setSelectedStatuses = (statuses: string[]) => {
    setSelectedStatusesState(statuses);
    try {
      if (statuses.length > 0) {
        localStorage.setItem(STORAGE_KEYS.DEMANDS_STATUS, JSON.stringify(statuses));
      } else {
        localStorage.removeItem(STORAGE_KEYS.DEMANDS_STATUS);
      }
    } catch (error) {
      // Silently fail
    }
  };

  const setSelectedQrCodeIdentifiers = (identifiers: string[]) => {
    setSelectedQrCodeIdentifiersState(identifiers);
    try {
      if (identifiers.length > 0) {
        localStorage.setItem(STORAGE_KEYS.DEMANDS_QRCODE_IDENTIFIER, JSON.stringify(identifiers));
      } else {
        localStorage.removeItem(STORAGE_KEYS.DEMANDS_QRCODE_IDENTIFIER);
      }
    } catch (error) {
      // Silently fail
    }
  };

  const setSelectedPriorities = (priorities: string[]) => {
    setSelectedPrioritiesState(priorities);
    try {
      if (priorities.length > 0) {
        localStorage.setItem(STORAGE_KEYS.DEMANDS_PRIORITY, JSON.stringify(priorities));
      } else {
        localStorage.removeItem(STORAGE_KEYS.DEMANDS_PRIORITY);
      }
    } catch (error) {
      // Silently fail
    }
  };

  const setSelectedAssigneeIds = (assigneeIds: string[]) => {
    setSelectedAssigneeIdsState(assigneeIds);
    try {
      if (assigneeIds.length > 0) {
        localStorage.setItem(STORAGE_KEYS.DEMANDS_ASSIGNEE, JSON.stringify(assigneeIds));
      } else {
        localStorage.removeItem(STORAGE_KEYS.DEMANDS_ASSIGNEE);
      }
    } catch (error) {
      // Silently fail
    }
  };

  const setSelectedProviderIds = (providerIds: string[]) => {
    setSelectedProviderIdsState(providerIds);
    try {
      if (providerIds.length > 0) {
        localStorage.setItem(STORAGE_KEYS.DEMANDS_PROVIDER, JSON.stringify(providerIds));
      } else {
        localStorage.removeItem(STORAGE_KEYS.DEMANDS_PROVIDER);
      }
    } catch (error) {
      // Silently fail
    }
  };

  const setDateRange = (range: DateRangeValue, page?: 'demands' | 'reports') => {
    setDateRangeState(range);
    // Se a página foi especificada, salvar no localStorage
    if (page) {
      saveDateRangeToStorage(page, range);
    }
  };

  const getStoredDateRange = (page: 'demands' | 'reports'): DateRangeValue | null => {
    return getDateRangeFromStorage(page);
  };

  const refreshDemands = () => {
    // Precisa devolver a Promise do callback registrado (loadDemands em useDemandsBoard) — todos
    // os `await refreshDemands()` espalhados pelo app (drag-and-drop, ações de demanda, criação)
    // dependiam de aguardar o refetch de verdade terminar antes de continuar, mas essa função
    // descartava a Promise e retornava undefined, resolvendo o await imediatamente. Isso fazia o
    // board não mostrar dados atualizados até um reload manual da página.
    if (refreshCallbackRef.current) {
      return refreshCallbackRef.current();
    }
  };

  const setRefreshCallback = (callback: (() => void | Promise<void>) | null) => {
    refreshCallbackRef.current = callback;
  };

  // Identidade estável entre renders — evita que consumidores que dependam dessas funções em
  // useEffect/useCallback entrem em loop (ex.: um efeito que chama refreshDemands() e, ao mudar
  // demandsData, provocaria um novo render do Provider e uma nova referência da função).
  const stableSetDemandsData = useStableCallback(setDemandsData);
  const stableSetSelectedClients = useStableCallback(setSelectedClients);
  const stableEnsureSingleClientSelected = useStableCallback(ensureSingleClientSelected);
  const stableSetDateRange = useStableCallback(setDateRange);
  const stableSetReportsDateType = useStableCallback(setReportsDateType);
  const stableSetDemandsDateType = useStableCallback(setDemandsDateType);
  const stableSetSelectedCategories = useStableCallback(setSelectedCategories);
  const stableSetSelectedStatuses = useStableCallback(setSelectedStatuses);
  const stableSetSelectedQrCodeIdentifiers = useStableCallback(setSelectedQrCodeIdentifiers);
  const stableSetSelectedPriorities = useStableCallback(setSelectedPriorities);
  const stableSetSelectedAssigneeIds = useStableCallback(setSelectedAssigneeIds);
  const stableSetSelectedProviderIds = useStableCallback(setSelectedProviderIds);
  const stableSetShowSimilarOnly = useStableCallback(setShowSimilarOnly);
  const stableSetSearchText = useStableCallback(setSearchText);
  const stableSetOverdueOnly = useStableCallback(setOverdueOnly);
  const stableSetChecklistPendingDemandIds = useStableCallback(setChecklistPendingDemandIds);
  const stableSetWhatsappPendingOnly = useStableCallback(setWhatsappPendingOnly);
  const stableRefreshDemands = useStableCallback(refreshDemands);
  const stableSetRefreshCallback = useStableCallback(setRefreshCallback);
  const stableGetStoredDateRange = useStableCallback(getStoredDateRange);

  const contextValue = useMemo<DemandsContextType>(() => ({
    demandsData,
    setDemandsData: stableSetDemandsData,
    selectedClients,
    setSelectedClients: stableSetSelectedClients,
    ensureSingleClientSelected: stableEnsureSingleClientSelected,
    dateRange,
    setDateRange: stableSetDateRange,
    reportsDateType,
    setReportsDateType: stableSetReportsDateType,
    demandsDateType,
    setDemandsDateType: stableSetDemandsDateType,
    selectedCategories,
    setSelectedCategories: stableSetSelectedCategories,
    selectedStatuses,
    setSelectedStatuses: stableSetSelectedStatuses,
    selectedQrCodeIdentifiers,
    setSelectedQrCodeIdentifiers: stableSetSelectedQrCodeIdentifiers,
    selectedPriorities,
    setSelectedPriorities: stableSetSelectedPriorities,
    selectedAssigneeIds,
    setSelectedAssigneeIds: stableSetSelectedAssigneeIds,
    selectedProviderIds,
    setSelectedProviderIds: stableSetSelectedProviderIds,
    showSimilarOnly,
    setShowSimilarOnly: stableSetShowSimilarOnly,
    searchText,
    setSearchText: stableSetSearchText,
    overdueOnly,
    setOverdueOnly: stableSetOverdueOnly,
    checklistPendingDemandIds,
    setChecklistPendingDemandIds: stableSetChecklistPendingDemandIds,
    whatsappPendingOnly, setWhatsappPendingOnly: stableSetWhatsappPendingOnly,
    refreshDemands: stableRefreshDemands,
    setRefreshCallback: stableSetRefreshCallback,
    getStoredDateRange: stableGetStoredDateRange
  }), [
    demandsData, stableSetDemandsData,
    selectedClients, stableSetSelectedClients, stableEnsureSingleClientSelected,
    dateRange, stableSetDateRange,
    reportsDateType, stableSetReportsDateType,
    demandsDateType, stableSetDemandsDateType,
    selectedCategories, stableSetSelectedCategories,
    selectedStatuses, stableSetSelectedStatuses,
    selectedQrCodeIdentifiers, stableSetSelectedQrCodeIdentifiers,
    selectedPriorities, stableSetSelectedPriorities,
    selectedAssigneeIds, stableSetSelectedAssigneeIds,
    selectedProviderIds, stableSetSelectedProviderIds,
    showSimilarOnly, stableSetShowSimilarOnly,
    searchText, stableSetSearchText,
    overdueOnly, stableSetOverdueOnly,
    checklistPendingDemandIds, stableSetChecklistPendingDemandIds,
    whatsappPendingOnly, stableSetWhatsappPendingOnly,
    stableRefreshDemands, stableSetRefreshCallback, stableGetStoredDateRange
  ]);

  return (
    <DemandsContext.Provider value={contextValue}>
      {children}
    </DemandsContext.Provider>
  );
};
